import { useState, useEffect } from 'react';
import { Button } from '@tv2/ui-system/components/Button';
import { CloseBasicNext } from '@tv2/ui-system/icons/next/navigation/CloseBasicNext';
import { useUserInteractionTrack } from '@tv2/lib-offspring/hooks/tracking/useUserInteractionTrack';
import { Link, LinkButton } from '@tv2/lib-offspring/components/link/Link';
import { aspectRatios } from '@tv2/lib-offspring/libs/image/aspectRatiosUtils';
import { ImgixLazyImage } from '@tv2/lib-offspring/components/image/ImgixLazyImage';
import Modal from '@tv2/lib-offspring/components/messages/Modal';
import { useUnknownTrack } from '@tv2/lib-offspring/hooks/tracking/useUnknownTrack';
import { useOverlayDisplayedTrack } from '@tv2/lib-offspring/hooks/tracking/useOverlayDisplayedTrack';
import { hide, isHidden } from './infoboxCache';
import { InfoBoxQuery } from './__generated__/InfoBox';

type InfoBox = Extract<
  Extract<InfoBoxQuery['promotionPage'], { __typename: 'PromotionPage' }>['infobox'],
  { __typename: 'CommercialInfoboxModal' }
>;

export default function CommercialInfoBoxModal({ infobox }: { infobox: InfoBox }) {
  const trackUserInteraction = useUserInteractionTrack();
  const [infoBoxDismissed, setInfoBoxDismissed] = useState(true);

  function dismissInfoBox(activeClose: boolean = false) {
    if (activeClose === true) {
      trackUserInteraction({ data: { label: 'close info-box', details: infobox?.title } });
    }
    hide(infobox);
    setInfoBoxDismissed(true);
  }

  useEffect(() => {
    const hideInfoBox = isHidden(infobox);
    if (!hideInfoBox) setInfoBoxDismissed(false);
  }, [infobox]);

  if (infoBoxDismissed || !infobox) return null;

  return <InfoboxModal dismissInfoBox={dismissInfoBox} infobox={infobox} />;
}
type InfoboxModalProps = {
  dismissInfoBox: (activeClose?: boolean) => void;
  infobox: InfoBox;
};

function InfoboxModal({ dismissInfoBox, infobox }: InfoboxModalProps) {
  const { title, text, button, image, link, showFooter } = infobox;
  const unknownTrack = useUnknownTrack();
  const textId = `info-box-description-${infobox.id}`;
  useOverlayDisplayedTrack({ data: { overlay: ['info-box'], headline: title ?? '' } });

  return (
    <Modal onClose={() => dismissInfoBox(true)}>
      <div
        className="relative flex flex-col my-0 mx-auto w-full w-max-[590px] w-max-[min(93vw, 590px)]"
        aria-describedby={textId}
        data-testid="infobox"
      >
        <div>
          <div
            role="button"
            onKeyDown={() => dismissInfoBox(true)}
            onClick={() => dismissInfoBox(true)}
            tabIndex={0}
            title="Luk info"
            className="block cursor-pointer w-[36px] h-[36px] z-[3] absolute overflow-visible top-4 right-4"
          >
            <div className="w-full h-full opacity-70 rounded-full bg-midnight-900 flex items-center justify-center">
              <CloseBasicNext className="invert" height={16} width={16} />
            </div>
          </div>
          {image ? (
            <ImgixLazyImage
              alt="presentation"
              src={image.url || ''}
              imgixOverrides={{ crop: 'top', fit: 'crop' }}
              aspectRatio={aspectRatios.dialog}
            ></ImgixLazyImage>
          ) : null}
        </div>
        <div className="bg-midnight-900 p-10">
          <section className="text-white pb-8">
            <span className="text-subheadline-s p-0 m-0">{title}</span>
            <span
              className="block pt-4 576:pt-5 whitespace-pre-line text-legacy-body-s"
              id={textId}
            >
              {text}
            </span>
          </section>
          <section className="flex flex-row gap-4 items-center justify-center my-0 mx-auto]">
            <div>
              <Button
                darkMode
                aria-label="Luk Dialog"
                color="secondary"
                onClick={() => {
                  dismissInfoBox(true);
                }}
              >
                Luk
              </Button>
            </div>
            <div>
              <LinkButton
                color="primary-red"
                href={button?.url ?? ''}
                onClick={(e: React.MouseEvent<Element>) => {
                  e.preventDefault();

                  if (button?.tracking?.identifier) {
                    unknownTrack({
                      event: button?.tracking?.identifier,
                      data: button?.tracking?.detail,
                    });
                  }

                  dismissInfoBox();

                  const url = button?.url;
                  if (url) {
                    setTimeout(() => {
                      window.location.href = url;
                    }, 200);
                  }
                }}
              >
                {button?.text ?? ''}
              </LinkButton>
            </div>
            {link?.text && link?.url && (
              <div>
                <Link
                  href={link?.url}
                  className="text-legacy-body-xs"
                  onClick={(e: React.MouseEvent<HTMLElement>) => {
                    e.preventDefault();

                    if (link.tracking?.identifier) {
                      unknownTrack({
                        event: link?.tracking?.identifier,
                        data: link?.tracking?.detail,
                      });
                    }

                    const linkurl = link?.url;
                    if (linkurl) {
                      setTimeout(() => {
                        window.location.href = linkurl;
                      }, 200);
                    }
                  }}
                >
                  {link.text}
                </Link>
              </div>
            )}
          </section>
          {showFooter && (
            <footer className="text-white text-legacy-body-xs pt-8 flex justify-center">
              <p>
                Brug for hjælp?{' '}
                <Link href="https://play.kundeservice.tv2.dk/hc/da">Gå til kundecenter</Link>
              </p>
            </footer>
          )}
        </div>
      </div>
    </Modal>
  );
}
